import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, FileText, Image, MessageSquare, Users, Mail, Calendar as CalIcon, CreditCard, Pencil, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const TABS = [
  { key: "bookings", label: "Prenotazioni", icon: CalIcon },
  { key: "payments", label: "Pagamenti", icon: CreditCard },
  { key: "blog", label: "Blog", icon: FileText },
  { key: "events", label: "Eventi", icon: Image },
  { key: "testimonials", label: "Testimonianze", icon: MessageSquare },
  { key: "contacts", label: "Messaggi", icon: Mail },
  { key: "newsletter", label: "Newsletter", icon: Users },
];

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-xs text-zinc-400 mb-1.5 block">{label}</label>
    <input {...props} className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#C19A5B]" />
  </div>
);

export const Admin = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  if (!loading && (!user || user.role !== "admin")) {
    navigate("/login");
    return null;
  }

  return (
    <div className="pt-28 pb-24 relative z-10 min-h-screen">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-display text-3xl text-white">Pannello di controllo</h1>
            <p className="text-zinc-500 text-sm mt-1">Benvenuto, {user?.name || "Admin"}</p>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-zinc-300 border border-white/15 rounded-full px-4 py-2 hover:border-[#C19A5B] hover:text-[#C19A5B] transition-colors text-sm">
            <LogOut size={16} /> Esci
          </button>
        </div>

        <Tabs defaultValue="blog">
          <TabsList className="bg-[#121212] border border-white/10 flex-wrap h-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`} className="data-[state=active]:bg-[#C19A5B] data-[state=active]:text-black gap-2">
                <t.icon size={15} /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="bookings"><BookingsAdmin qc={qc} /></TabsContent>
          <TabsContent value="payments"><PaymentsAdmin /></TabsContent>
          <TabsContent value="blog"><BlogAdmin qc={qc} /></TabsContent>
          <TabsContent value="events"><EventsAdmin qc={qc} /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsAdmin qc={qc} /></TabsContent>
          <TabsContent value="contacts"><MessagesAdmin qc={qc} /></TabsContent>
          <TabsContent value="newsletter"><ReadOnlyList endpoint="/newsletter" columns={[["email", "Email"], ["created_at", "Data iscrizione"]]} testid="newsletter" /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const EMPTY_POST = { title: "", excerpt: "", content: "", cover_image: "", category: "Mindset", published: true };

const BlogAdmin = ({ qc }) => {
  const { data: posts = [] } = useQuery({ queryKey: ["blog", "admin"], queryFn: () => api.get("/blog?admin=true").then((r) => r.data) });
  const [form, setForm] = useState(EMPTY_POST);
  const [editingId, setEditingId] = useState(null);
  const empty = EMPTY_POST;

  const create = useMutation({
    mutationFn: () => (editingId ? api.put(`/blog/${editingId}`, form) : api.post("/blog", form)),
    onSuccess: () => {
      toast.success(editingId ? "Articolo aggiornato" : "Articolo pubblicato");
      setForm(EMPTY_POST);
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["blog"] });
    },
    onError: () => toast.error("Errore nel salvataggio"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/blog/${id}`),
    onSuccess: () => { toast.success("Eliminato"); qc.invalidateQueries({ queryKey: ["blog"] }); },
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid lg:grid-cols-2 gap-8 mt-8">
      <div className="bg-[#121212] border border-white/10 rounded-xl p-6 space-y-4 h-fit">
        <h3 className="text-white font-medium flex items-center gap-2"><Plus size={16} /> {editingId ? "Modifica articolo" : "Nuovo articolo"}</h3>
        <Field label="Titolo" value={form.title} onChange={upd("title")} data-testid="blog-title-input" />
        <Field label="Categoria" value={form.category} onChange={upd("category")} data-testid="blog-category-input" />
        <Field label="Immagine di copertina (URL)" value={form.cover_image} onChange={upd("cover_image")} data-testid="blog-image-input" />
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">Estratto</label>
          <textarea value={form.excerpt} onChange={upd("excerpt")} rows={2} data-testid="blog-excerpt-input" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#C19A5B] resize-none" />
        </div>
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">Contenuto</label>
          <textarea value={form.content} onChange={upd("content")} rows={8} data-testid="blog-content-input" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#C19A5B] resize-none" />
        </div>
        <div className="flex gap-2">
          <button onClick={() => create.mutate()} disabled={!form.title || create.isPending} data-testid="blog-save-btn" className="flex-1 rounded-full bg-[#C19A5B] text-black font-semibold py-3 hover:bg-[#D4AF37] transition-colors disabled:opacity-50">
            {create.isPending ? "Salvataggio…" : editingId ? "Aggiorna articolo" : "Pubblica articolo"}
          </button>
          {editingId && <button onClick={() => { setForm(empty); setEditingId(null); }} className="rounded-full border border-white/20 text-zinc-300 px-4 py-3 hover:border-[#C19A5B]" data-testid="blog-cancel-btn">Annulla</button>}
        </div>
      </div>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-blog-item-${p.id}`} className="bg-[#121212] border border-white/10 rounded-xl p-4 flex items-center gap-4">
            {p.cover_image && <img src={p.cover_image} alt="" loading="lazy" decoding="async" className="h-14 w-14 rounded-md object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-white truncate">{p.title}</p>
              <p className="text-zinc-500 text-xs">{p.category}</p>
            </div>
            <button onClick={async () => { try { const full = (await api.get(`/blog/${p.slug}`)).data; setForm({ title: full.title, excerpt: full.excerpt, content: full.content, cover_image: full.cover_image, category: full.category, published: full.published }); setEditingId(p.id); window.scrollTo({ top: 0, behavior: "smooth" }); } catch { toast.error("Errore nel caricamento"); } }} data-testid={`admin-blog-edit-${p.id}`} className="text-zinc-500 hover:text-[#C19A5B]"><Pencil size={16} /></button>
            <button onClick={() => del.mutate(p.id)} data-testid={`admin-blog-delete-${p.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 size={17} /></button>
          </div>
        ))}
        {posts.length === 0 && <p className="text-zinc-600 text-sm">Nessun articolo.</p>}
      </div>
    </div>
  );
};

const EventsAdmin = ({ qc }) => {
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  const [form, setForm] = useState({ title: "", description: "", image_url: "", location: "", event_date: "" });
  const create = useMutation({
    mutationFn: () => api.post("/events", form),
    onSuccess: () => { toast.success("Evento aggiunto"); setForm({ title: "", description: "", image_url: "", location: "", event_date: "" }); qc.invalidateQueries({ queryKey: ["events"] }); },
    onError: () => toast.error("Errore"),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/events/${id}`), onSuccess: () => { toast.success("Eliminato"); qc.invalidateQueries({ queryKey: ["events"] }); } });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid lg:grid-cols-2 gap-8 mt-8">
      <div className="bg-[#121212] border border-white/10 rounded-xl p-6 space-y-4 h-fit">
        <h3 className="text-white font-medium flex items-center gap-2"><Plus size={16} /> Nuovo evento</h3>
        <Field label="Titolo" value={form.title} onChange={upd("title")} data-testid="event-title-input" />
        <Field label="Immagine (URL)" value={form.image_url} onChange={upd("image_url")} data-testid="event-image-input" />
        <Field label="Luogo" value={form.location} onChange={upd("location")} data-testid="event-location-input" />
        <Field label="Anno / Data" value={form.event_date} onChange={upd("event_date")} data-testid="event-date-input" />
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">Descrizione</label>
          <textarea value={form.description} onChange={upd("description")} rows={3} data-testid="event-desc-input" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#C19A5B] resize-none" />
        </div>
        <button onClick={() => create.mutate()} disabled={!form.title || !form.image_url || create.isPending} data-testid="event-save-btn" className="w-full rounded-full bg-[#C19A5B] text-black font-semibold py-3 hover:bg-[#D4AF37] transition-colors disabled:opacity-50">Aggiungi evento</button>
      </div>
      <div className="grid grid-cols-2 gap-3 h-fit">
        {events.map((e) => (
          <div key={e.id} data-testid={`admin-event-item-${e.id}`} className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden relative group">
            <img src={e.image_url} alt="" className="h-28 w-full object-cover" />
            <div className="p-3"><p className="text-white text-sm truncate">{e.title}</p><p className="text-zinc-500 text-xs">{e.location}</p></div>
            <button onClick={() => del.mutate(e.id)} data-testid={`admin-event-delete-${e.id}`} className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 text-zinc-300 hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const TestimonialsAdmin = ({ qc }) => {
  const { data: items = [] } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", role: "", text: "", photo_url: "", rating: 5 });
  const create = useMutation({
    mutationFn: () => api.post("/testimonials", { ...form, rating: Number(form.rating) }),
    onSuccess: () => { toast.success("Testimonianza aggiunta"); setForm({ name: "", role: "", text: "", photo_url: "", rating: 5 }); qc.invalidateQueries({ queryKey: ["testimonials"] }); },
    onError: () => toast.error("Errore"),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/testimonials/${id}`), onSuccess: () => { toast.success("Eliminato"); qc.invalidateQueries({ queryKey: ["testimonials"] }); } });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid lg:grid-cols-2 gap-8 mt-8">
      <div className="bg-[#121212] border border-white/10 rounded-xl p-6 space-y-4 h-fit">
        <h3 className="text-white font-medium flex items-center gap-2"><Plus size={16} /> Nuova testimonianza</h3>
        <Field label="Nome" value={form.name} onChange={upd("name")} data-testid="testi-name-input" />
        <Field label="Ruolo" value={form.role} onChange={upd("role")} data-testid="testi-role-input" />
        <Field label="Foto (URL)" value={form.photo_url} onChange={upd("photo_url")} data-testid="testi-photo-input" />
        <Field label="Valutazione (1-5)" type="number" min="1" max="5" value={form.rating} onChange={upd("rating")} data-testid="testi-rating-input" />
        <div>
          <label className="text-xs text-zinc-400 mb-1.5 block">Testo</label>
          <textarea value={form.text} onChange={upd("text")} rows={4} data-testid="testi-text-input" className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#C19A5B] resize-none" />
        </div>
        <button onClick={() => create.mutate()} disabled={!form.name || !form.text || create.isPending} data-testid="testi-save-btn" className="w-full rounded-full bg-[#C19A5B] text-black font-semibold py-3 hover:bg-[#D4AF37] transition-colors disabled:opacity-50">Aggiungi</button>
      </div>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testi-item-${t.id}`} className="bg-[#121212] border border-white/10 rounded-xl p-4 flex items-center gap-4">
            {t.photo_url && <img src={t.photo_url} alt="" className="h-12 w-12 rounded-full object-cover" />}
            <div className="flex-1 min-w-0"><p className="text-white text-sm">{t.name}</p><p className="text-zinc-500 text-xs line-clamp-1">{t.text}</p></div>
            <button onClick={() => del.mutate(t.id)} data-testid={`admin-testi-delete-${t.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 size={17} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const STATUSES = ["richiesta", "confermata", "completata", "annullata"];

const BookingsAdmin = ({ qc }) => {
  const { data: rows = [] } = useQuery({ queryKey: ["bookings"], queryFn: () => api.get("/bookings").then((r) => r.data) });
  const [replies, setReplies] = useState({});
  const patch = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/bookings/${id}`, body),
    onSuccess: () => { toast.success("Aggiornato"); qc.invalidateQueries({ queryKey: ["bookings"] }); },
    onError: () => toast.error("Errore"),
  });
  return (
    <div className="mt-8 space-y-4" data-testid="admin-bookings">
      {rows.length === 0 && <p className="text-zinc-600 text-sm">Nessuna prenotazione ancora.</p>}
      {rows.map((b) => (
        <div key={b.id} data-testid={`booking-${b.id}`} className="bg-[#121212] border border-white/10 rounded-xl p-5">
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <p className="text-white font-medium">{b.name} <span className="text-zinc-500 text-sm">· {b.email} · {b.phone}</span></p>
              <p className="text-[#C19A5B] text-sm mt-1">{b.session_label} · {b.date} {b.time} {b.price > 0 ? `· €${b.price}` : "· gratis"}</p>
              {b.notes && <p className="text-zinc-400 text-sm mt-1">{b.notes}</p>}
            </div>
            <div className="flex items-center gap-2 h-fit">
              <span className={`text-xs px-2.5 py-1 rounded-full ${b.payment_status === "paid" ? "bg-green-500/15 text-green-400" : b.payment_status === "n/a" ? "bg-white/10 text-zinc-400" : "bg-yellow-500/15 text-yellow-400"}`}>{b.payment_status === "paid" ? "Pagato" : b.payment_status === "n/a" ? "Gratuito" : "Da pagare"}</span>
              <Select value={b.status} onValueChange={(v) => patch.mutate({ id: b.id, body: { status: v } })}>
                <SelectTrigger data-testid={`booking-status-${b.id}`} className="h-8 w-36 bg-[#0A0A0A] border-[#27272A] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <input value={replies[b.id] ?? b.reply ?? ""} onChange={(e) => setReplies({ ...replies, [b.id]: e.target.value })} placeholder="Nota / risposta per il cliente…" className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 text-sm outline-none focus:border-[#C19A5B]" data-testid={`booking-reply-${b.id}`} />
            <button onClick={() => patch.mutate({ id: b.id, body: { reply: replies[b.id] ?? b.reply ?? "" } })} className="rounded-md bg-[#C19A5B] text-black px-3 py-2 text-sm hover:bg-[#D4AF37]" data-testid={`booking-reply-send-${b.id}`}><Send size={14} /></button>
          </div>
          {b.reply && <p className="text-zinc-500 text-xs mt-2">Nota salvata: {b.reply}</p>}
        </div>
      ))}
    </div>
  );
};

const PaymentsAdmin = () => {
  const { data: rows = [] } = useQuery({ queryKey: ["payments"], queryFn: () => api.get("/payments").then((r) => r.data) });
  const total = rows.filter((r) => r.payment_status === "paid").reduce((s, r) => s + r.amount, 0);
  return (
    <div className="mt-8" data-testid="admin-payments">
      <div className="bg-[#121212] border border-[#C19A5B]/30 rounded-xl p-6 mb-5">
        <p className="text-zinc-400 text-sm">Incassato (pagamenti confermati)</p>
        <p className="font-display text-4xl gold-text mt-1">€{total.toFixed(2)}</p>
      </div>
      <div className="bg-[#121212] border border-white/10 rounded-xl divide-y divide-white/5">
        {rows.length === 0 && <p className="text-zinc-600 text-sm p-6">Nessun pagamento registrato.</p>}
        {rows.map((p) => (
          <div key={p.id} className="p-4 flex flex-wrap justify-between gap-2">
            <div><p className="text-white text-sm">{p.session_label}</p><p className="text-zinc-500 text-xs">{p.customer_name} · {p.customer_email}</p></div>
            <div className="text-right"><p className="text-white">€{p.amount.toFixed(2)} <span className="uppercase text-xs text-zinc-500">{p.currency}</span></p><span className={`text-xs ${p.payment_status === "paid" ? "text-green-400" : "text-yellow-400"}`}>{p.payment_status === "paid" ? "Pagato" : p.payment_status === "pending" ? "In attesa" : p.payment_status === "expired" ? "Scaduto" : p.payment_status}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MessagesAdmin = ({ qc }) => {
  const { data: rows = [] } = useQuery({ queryKey: ["/contacts"], queryFn: () => api.get("/contacts").then((r) => r.data) });
  const [replies, setReplies] = useState({});
  const patch = useMutation({
    mutationFn: ({ id, reply }) => api.patch(`/contacts/${id}`, { reply }),
    onSuccess: () => { toast.success("Risposta salvata"); qc.invalidateQueries({ queryKey: ["/contacts"] }); },
    onError: () => toast.error("Errore"),
  });
  return (
    <div className="mt-8 space-y-4" data-testid="admin-list-contacts">
      {rows.length === 0 && <p className="text-zinc-600 text-sm">Nessun messaggio.</p>}
      {rows.map((c) => (
        <div key={c.id} className="bg-[#121212] border border-white/10 rounded-xl p-5">
          <p className="text-white font-medium">{c.name} <span className="text-zinc-500 text-sm">· {c.email} · {c.phone}</span></p>
          <p className="text-zinc-300 text-sm mt-2">{c.message}</p>
          <div className="mt-4 flex gap-2">
            <input value={replies[c.id] ?? c.reply ?? ""} onChange={(e) => setReplies({ ...replies, [c.id]: e.target.value })} placeholder="Rispondi al cliente…" className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 text-sm outline-none focus:border-[#C19A5B]" data-testid={`contact-reply-${c.id}`} />
            <button onClick={() => patch.mutate({ id: c.id, reply: replies[c.id] ?? c.reply ?? "" })} className="rounded-md bg-[#C19A5B] text-black px-3 py-2 text-sm hover:bg-[#D4AF37]" data-testid={`contact-reply-send-${c.id}`}><Send size={14} /></button>
          </div>
          {c.reply && <p className="text-zinc-500 text-xs mt-2">Risposta salvata: {c.reply}</p>}
        </div>
      ))}
    </div>
  );
};

const ReadOnlyList = ({ endpoint, columns, testid }) => {
  const { data: rows = [] } = useQuery({ queryKey: [endpoint], queryFn: () => api.get(endpoint).then((r) => r.data) });
  return (
    <div className="mt-8 bg-[#121212] border border-white/10 rounded-xl overflow-hidden" data-testid={`admin-list-${testid}`}>
      {rows.length === 0 ? (
        <p className="text-zinc-600 text-sm p-6">Nessun dato.</p>
      ) : (
        <div className="divide-y divide-white/5">
          {rows.map((r) => (
            <div key={r.id} className="p-4 grid gap-1">
              {columns.map(([key, label]) => (
                <p key={key} className="text-sm">
                  <span className="text-zinc-500">{label}: </span>
                  <span className="text-zinc-200">{key === "created_at" ? fmtDate(r[key]) : r[key]}</span>
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Admin;
